/**
 * Default text/explanation translations for Option objects used in
 * single-choice-question steps (e.g. "Digitally" vs "Manually" choices).
 *
 * Keyed by a compound path that disambiguates the same option name across
 * different step contexts (e.g. 'assets.digital' vs 'income.digital').
 *
 * Parallels FIELD_LABEL_DEFAULTS for FieldConfig labels. Admins can still
 * override any option label per-language via the flow editor (_i18n on the
 * Option) — these defaults are only consulted when no override exists.
 *
 * The compound path is stored on the Option as `_defaultsKey` so it survives
 * server round-trip and remains available for tier-2 lookup.
 */
export const OPTION_LABEL_DEFAULTS: Record<string, Record<string, { text?: string; explanation?: string }>> = {

  // ── Employment / Income: digital vs manual ───────────────────
  "income.digital": {
    es: {
      text: "Digitalmente",
      explanation: "Verifique sus ingresos de forma electrónica segura, eliminando la necesidad de copias en papel",
    },
  },
  "income.manual": {
    es: {
      text: "Manualmente",
      explanation: "Ingrese manualmente la información de empleo e ingresos y proporcione los extractos en papel más tarde",
    },
  },

  // ── Assets: digital vs manual ────────────────────────────────
  "assets.digital": {
    es: {
      text: "Digitalmente",
      explanation: "Recopile sus extractos bancarios de forma electrónica segura, eliminando la necesidad de copias en papel",
    },
  },
  "assets.manual": {
    es: {
      text: "Manualmente",
      explanation: "Ingrese manualmente la información de sus activos y proporcione los extractos en papel más tarde",
    },
  },

  // ── Loan purpose options (server-provided, keyed by mortgageLoanPurpose enum) ─
  "loanPurpose.Purchase": {
    es: {
      text: "Comprando una casa",
      explanation: "Si ha encontrado una casa o necesita una preaprobación, haga clic aquí.",
    },
  },
  "loanPurpose.Refinance": {
    es: {
      text: "Refinanciando",
      explanation: "Si actualmente es propietario y desea cambiar los términos de su hipoteca, haga clic aquí.",
    },
  },
  "loanPurpose.Construction": {
    es: {
      text: "Construcción",
    },
  },
  "loanPurpose.ConstructionOnly": {
    es: {
      text: "Solo construcción",
    },
  },
  "loanPurpose.ConstructionToPermanent": {
    es: {
      text: "Construcción a permanente",
    },
  },
  "loanPurpose.MortgageModification": {
    es: {
      text: "Modificación de hipoteca",
    },
  },
  "loanPurpose.HomeEquity": {
    es: {
      text: "Préstamo sobre el valor líquido de la vivienda",
    },
  },

  // ── Current application state options ────────────────────────
  // Keyed by option.name (hyphenated) since values are booleans here.
  "appState.purchase-agreement": {
    es: {
      text: "Tiene un acuerdo de compra firmado",
    },
  },
  "appState.pre-approval": {
    es: {
      text: "Buscando obtener preaprobación",
    },
  },

  // ── Occupancy options (keyed by option.value enum) ───────────
  "occupancy.PrimaryResidence": {
    es: { text: "Residencia principal" },
  },
  "occupancy.SecondaryResidence": {
    es: { text: "Vacaciones/Segunda vivienda" },
  },
  "occupancy.Investment": {
    es: { text: "Propiedad de inversión" },
  },

  // ── Property type options (keyed by option.value enum) ───────
  "propertyType.SFR": {
    es: { text: "Vivienda unifamiliar" },
  },
  "propertyType.Townhouse": {
    es: { text: "Casa adosada" },
  },
  "propertyType.Condominium": {
    es: { text: "Condominio" },
  },
  "propertyType.MultiFamilyResidence": {
    es: { text: "Multifamiliar" },
  },
  "propertyType.ManufacturedHousing": {
    es: { text: "Vivienda prefabricada" },
  },
  "propertyType.Cooperative": {
    es: { text: "Cooperativa" },
  },

  // ── Lead source / "How did you hear about us?" options ───────
  "leadSource.socialMedia":     { es: { text: "Redes sociales" } },
  "leadSource.realEstateAgent": { es: { text: "Agente inmobiliario" } },
  "leadSource.internet":        { es: { text: "Internet" } },
  "leadSource.pastClients":     { es: { text: "Referencia personal" } },
  "leadSource.returningClient": { es: { text: "Cliente que regresa" } },
  "leadSource.radio":           { es: { text: "Radio" } },
  "leadSource.letterInTheMail": { es: { text: "Carta por correo" } },
  "leadSource.other":           { es: { text: "Otro" } },
};
